import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    Output
} from "@angular/core";

interface CheckboxTreeItem {
    label: string;
    checked: boolean;
    type: string;
    parent: string;
    value: string;
}

interface Leaf extends CheckboxTreeItem {
    type: "leaf";
}

interface Branch extends CheckboxTreeItem {
    type: "branch";
    children: Array<CheckboxTreeItem>;
    collapsed: boolean;
    indeterminate: boolean;
    isLast: boolean;
}

@Component({
    selector: "deloitte-ai-checkboxtree",
    templateUrl: "./checkbox-tree.html",
    styleUrls: ["./checkbox-tree.scss"],
})
export class CheckboxTreeComponent implements AfterViewInit {
    private _root: Branch;
    private _data: any;
    private _selected: any;
    private initialized = false;

    @Output()
    selectionChange: EventEmitter<any> = new EventEmitter();

    @Input()
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    set data(data: any) {
        if (data) {
            this._data = Array.isArray(data)
                ? ((this.root.isLast = true), this.createLeaf(data, "0"))
                : this.convertData(data);

            this.root.children = this._data;

            if (this.initialized) {
                this.prepareData();
            }
        }
    }

    @Input()
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    set selected(data: any) {
        if (data) {
            this._selected = this.convertData(data);
        }
    }

    // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
    get data(): any {
        return this._data;
    }

    // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
    get root(): Branch {
        if (!this._root) {
            this._root = this.createBranch("~", null);
            this._root.indeterminate = true;
        }
        return this._root;
    }

    // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
    get selected(): any {
        return this._selected;
    }

    ngAfterViewInit(): void {
        this.initialized = true;
    }

    prepareData(): any {
        if (!this.data || !this.selected) {
            return;
        }
        this.mapSelectionToCheckboxTree(this.selected, []);
    }

    reset(): void {
        this.resetTree(this.root);
    }

    toggleBranch(item: Branch, $event: MouseEvent): void {
        $event.stopPropagation();
        $event.preventDefault();

        item.collapsed = !item.collapsed;
    }

    selectBranch(item: Branch): void {
        item.indeterminate = false;
        this.toggleItemState(item, !item.checked);
        this.toggleInderminateState(this.resolveParent(item.parent), false);

        this.selectData(item);
    }

    toggleLeaf(item: Leaf): void {
        item.checked = !item.checked;

        const parentNode = this.resolveParent(item.parent);

        this.toggleInderminateState(parentNode, false);
        this.selectData(item);
    }

    private mapSelectionToCheckboxTree(selected: any, path: string[]): any {
        for (let i = 0, ln = selected.length; i < ln; i++) {
            const node = [...path, selected[i].value];
            const source = this.findSource(node, this.data);

            if (source.type === "branch") {
                source.children.length === selected[i].children.length
                    ? (source.checked = true)
                    : (source.indeterminate = true);

                this.mapSelectionToCheckboxTree(selected[i].children, node);
                continue;
            }
            source.checked = true;
        }
    }

    private resetTree(node: Branch): any {
        node.checked = false;
        node.indeterminate = false;

        for (let i = 0, ln = node.children.length; i < ln; i++) {
            const item = node.children[i];

            const isBranch = item.type === "branch";
            const isChecked = (item as Branch).checked;
            const isIndeterminate = (item as Branch).indeterminate;

            if (isBranch && (isChecked || isIndeterminate)) {
                this.resetTree(item as Branch);
            } else {
                item.checked = false;
            }
        }
    }

    private convertData(source: any, path = "~"): any {
        const keys = Object.keys(source);
        const result = [];

        for (let i = 0, ln = keys.length; i < ln; i++) {
            const value = source[keys[i]];
            const branch = this.createBranch(keys[i], path);
            const parent = path.concat("/", i.toString());

            if (!Array.isArray(value)) {
                branch.children = this.convertData(value, parent);
            } else {
                branch.isLast = true;
                branch.children = this.createLeaf(value, parent);
            }

            result.push(branch);
        }

        return result;
    }

    private createBranch(name: string, parent: string): Branch {
        const branch: Branch = {
            label: name,
            children: [],
            collapsed: true,
            checked: false,
            type: "branch",
            indeterminate: false,
            isLast: false,
            parent,
            value: name,
        };

        return branch;
    }

    private createLeaf(values: string[], parent: string): Leaf[] {
        const leafs: Leaf[] = [];
        for (let i = 0, ln = values.length; i < ln; i++) {
            leafs.push({
                label: values[i],
                checked: false,
                type: "leaf",
                value: values[i],
                parent,
            });
        }
        return leafs;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private selectData(item: CheckboxTreeItem): any {
        const selectionChangeResult = this.resolveSelectionChangeResult();
        this.selectionChange.emit(selectionChangeResult);
    }

    private resolveSelectionChangeResult(): any {
        // fix: this only takes the possibility of 2 levels into account !!!
        const isMultiLevel = "children" in this.root.children[0];

        if (isMultiLevel) {
            return this.root.children
                .filter((filter) => {
                    if (filter.checked) {
                        return true;
                    }
                    const checkedChildren = (filter as any).children.filter(
                        (child) => child.checked
                    );

                    if (checkedChildren.length > 0) {
                        return true;
                    }

                    return false;
                })
                .reduce((acc, filter) => {
                    if (filter.checked) {
                        acc[filter.label] = (filter as any).children.map(
                            (child) => child.label
                        );

                        return acc;
                    }

                    acc[filter.label] = (filter as any).children
                        .filter((child) => child.checked)
                        .map((child) => child.label);

                    return acc;
                }, {});
        }

        return this.root.children
            .filter((filter) => filter.checked)
            .map((filter) => filter.label);
    }

    private toggleItemState(item: CheckboxTreeItem, state: any): any {
        if (item.checked === state) {
            return;
        }

        item.checked = state;

        if (item.type === "branch") {
            const node: Branch = item as Branch;
            node.indeterminate = false;

            for (let i = 0, ln = node.children.length; i < ln; i++) {
                this.toggleItemState(node.children[i], state);
            }
        }
    }

    private toggleInderminateState(parent: Branch = null, indeterminate): any {
        if (!parent) {
            return;
        }

        const checkedChildren = parent.children.filter(
            (item: CheckboxTreeItem) => item.checked
        );
        const allChecked = checkedChildren.length === parent.children.length;
        const isIndeterminate =
            (!allChecked && checkedChildren.length > 0) || indeterminate;

        parent.indeterminate = isIndeterminate;
        parent.checked = allChecked;
        this.toggleInderminateState(
            this.resolveParent(parent.parent),
            isIndeterminate
        );
    }

    private resolveParent(path: string): Branch | undefined {
        if (path === "/") {
            return void 0;
        }

        const segments = path.split("/").slice(1);
        let source = this.data;
        let parent;

        do {
            const next = segments.shift();
            if (next === undefined) {
                break;
            }

            const segment = parseInt(next, 10);
            parent = source[segment];
            source = parent.children;
        } while (segments.length);

        return parent;
    }

    private findSource(segment: string[], root: CheckboxTreeItem[]): any {
        const needles = [...segment];
        let source = root;
        let result;

        do {
            const needle = needles.shift();
            source.forEach((item) => {
                if (item.value === needle) {
                    source = (item as Branch).children;
                    result = item;
                    return;
                }
            });
        } while (needles.length);
        return result;
    }
}
